import { useState } from 'react';
import styled, { css, type RuleSet } from 'styled-components';
import { Spacing8 } from 'autogen/design-tokens/tokens';
import Icon from 'components/Icon';
import useElementSizeEffect from 'hooks/globals/useElementSizeEffect';
import { invisibleScrollbar } from 'modules/helpers/mixins';
import { breakpoint, sizes } from 'variables';
import ScrollSnapSliderContext from '../context/ScrollSnapSliderContext';
import useScrollSnapSlider from '../hooks/useScrollSnapSlider';
import ScrollSnapSlider from '../ScrollSnapSlider';
import NavigationFadeButton from './NavigationFadeButton';

const defaultItemSize = 260;

const RootContainer = styled.div`
    position: relative;
    min-width: 0;

    @media print {
        display: none;
    }
`;
const StaticContainer = styled.div<{ $itemsPerSlide: number; $gapSize: string; $loading: boolean; $itemSize: number }>`
    && {
        display: grid;
        grid-auto-flow: column;
        grid-auto-columns: 100%;

        ${breakpoint.up(sizes.sm)} {
            grid-auto-columns: ${({ $itemsPerSlide, $gapSize }) =>
                `calc(100% / ${Math.max(1, $itemsPerSlide)} - ${$gapSize} + calc(${$gapSize} / ${Math.max(
                    1,
                    $itemsPerSlide
                )}))`};
        }

        ${({ $itemSize, $loading }) =>
            $loading &&
            css`
                & > * {
                    max-width: ${`${$itemSize}px`};
                }
        `}
    }
`;

const StyledScrollSnapSlider = styled(ScrollSnapSlider)<{
    $itemsPerSlide: number;
    $loading: boolean;
    $gapSize: string;
    $teaseNextItem: boolean;
    $itemSize: number;
    $nextItemMobileIndentation?: string;
}>`
    && {
        display: grid;
        grid-auto-flow: column;
        ${({ $gapSize }) => `gap: ${$gapSize};`}
        scroll-snap-type: none;
        scroll-snap-stop: normal;

        ${({ $itemsPerSlide, $nextItemMobileIndentation, $teaseNextItem, $gapSize }) =>
            $nextItemMobileIndentation
                ? css`
                      grid-auto-columns: ${`calc(100% / ${$itemsPerSlide} - ${$nextItemMobileIndentation})`};
                  `
                : css`
                      grid-auto-columns: ${`calc(100% / calc(${$itemsPerSlide}${
                          $teaseNextItem ? '.5' : ''
                      }) - calc(calc(${$gapSize} * ${$itemsPerSlide - 1}) / ${$itemsPerSlide}))`};
                  `}

        ${breakpoint.up(sizes.sm)} {
            grid-auto-columns: ${({ $itemsPerSlide, $teaseNextItem, $gapSize }) =>
                `calc(100% / calc(${$itemsPerSlide}${$teaseNextItem ? '.5' : ''}) - calc(calc(${$gapSize} * ${
                    $itemsPerSlide - 1
                }) / ${$itemsPerSlide}))`};
        }

        ${breakpoint.up(sizes.xl)} {
            scroll-snap-type: x mandatory;
            scroll-snap-stop: always;
            grid-auto-columns: ${({ $itemsPerSlide, $gapSize }) =>
                `calc(100% / ${$itemsPerSlide} - ${$gapSize} + calc(${$gapSize} / ${$itemsPerSlide}))`};
        }


        ${invisibleScrollbar()}
    
        ${({ $loading }) =>
            $loading &&
            css`
                & {
                    visibility: hidden;
                    grid-auto-columns: 1fr;
                }
            `}

        ${({ $itemSize, $loading }) =>
            $loading &&
            css`
                & > * {
                    max-width: ${`${$itemSize}px`};
                }
        `}
    }    
`;

const options = {
    draggable: true,
    loop: false
};

type Props<T> = {
    items: T[];
    itemRenderer: (item: T, index: number) => JSX.Element | JSX.Element[] | null;
    itemSize?: number;
    hideCarouselButtons?: boolean;
    scrollContainerCss?: RuleSet;
    nextItemMobileIndentation?: string;
    gapSize?: string;
};

/*
    The DynamicItemCarousel will adjust the number of items per slide automatically based on a provided itemSize.
    If this behavior is not needed and the number of items per slide is static or based on window width, use the itemCarousel instead.
*/
export default function DynamicItemCarousel<T>({
    items,
    itemRenderer,
    itemSize = defaultItemSize,
    hideCarouselButtons = false,
    scrollContainerCss,
    nextItemMobileIndentation,
    gapSize = Spacing8,
    ...restProps
}: Props<T>) {
    const { ref, slider } = useScrollSnapSlider(options);
    const [itemsPerSlide, setItemsPerSlide] = useState<number>(0);
    const [loading, setLoading] = useState(true);
    const [teaseNextSlide, setTeaseNextSlide] = useState(false);
    const getItemsThatCanFitInContainer = (containerWidth, itemWidth) =>
        Math.max(Math.floor(containerWidth / itemWidth), 1);
    const maxItemsPerSlide = 6;

    const [sizeRef] = useElementSizeEffect((size) => {
        const itemsThatCanFitInContainer = getItemsThatCanFitInContainer(size.width, itemSize);
        const nextState = Math.min(itemsThatCanFitInContainer, maxItemsPerSlide);
        setItemsPerSlide(nextState);
        setLoading(false);

        if (ref.current) {
            setTeaseNextSlide(items.length > itemsThatCanFitInContainer);
        }
    });

    const attachRefs = (node) => {
        ref.current = node;
        sizeRef.current = node;
    };

    if (!items.length) return null;

    if (items.length === 1)
        return (
            <RootContainer {...restProps}>
                <StaticContainer
                    ref={attachRefs}
                    $itemsPerSlide={itemsPerSlide}
                    $gapSize={gapSize}
                    $loading={loading}
                    $itemSize={itemSize}
                    css={scrollContainerCss}
                >
                    {items.map((item, index) => itemRenderer(item, index))}
                </StaticContainer>
            </RootContainer>
        );

    return (
        <ScrollSnapSliderContext.Provider value={slider}>
            <RootContainer {...restProps}>
                {!hideCarouselButtons && (
                    <>
                        <NavigationFadeButton direction="next" slideAmount={itemsPerSlide} size="L" withBorder />
                        <NavigationFadeButton direction="prev" slideAmount={itemsPerSlide} size="L" withBorder />
                    </>
                )}

                <StyledScrollSnapSlider
                    css={scrollContainerCss}
                    $loading={loading}
                    $itemsPerSlide={itemsPerSlide}
                    $teaseNextItem={teaseNextSlide}
                    $nextItemMobileIndentation={nextItemMobileIndentation}
                    $gapSize={gapSize}
                    $itemSize={itemSize}
                    ref={attachRefs}
                >
                    {items.map((item, index) => itemRenderer(item, index))}
                </StyledScrollSnapSlider>
            </RootContainer>
        </ScrollSnapSliderContext.Provider>
    );
}
